import styled, { css, DefaultTheme } from 'styled-components';
import { media } from 'styles/utils';

interface TypographyProps {
  fontSize: number;
  lineHeight: number;

  fontSizeDesktop: number;
  lineHeightDesktop: number;

  fontFamily: string;
  fontWeight: number;

  letterSpacing?: number;
  textTransform?: string;
}

interface ComponentProps {
  margin?: string;
  color?:
    | 'brandPrimary'
    | 'brandSecondary'
    | 'brandTertiary'
    | 'actionConvertionStatic'
    | 'actionConvertionHover'
    | 'actionConvertionDisabled'
    | 'actionNavigationStandardStatic'
    | 'actionNavigationStandardHover'
    | 'actionNavigationStandardDisabled'
    | 'actionNavigationAltStatic'
    | 'actionNavigationAltHover'
    | 'actionNavigationAltDisabled'
    | 'tertiaryColor01'
    | 'tertiaryColor02'
    | 'tertiaryColor03'
    | 'tertiaryColor04'
    | 'tertiaryColor05'
    | 'tertiaryColor06'
    | 'tertiaryColor07'
    | 'tertiaryColor08'
    | 'tertiaryColor09'
    | 'tertiaryColor10'
    | 'tertiaryColor11'
    | 'neutralUltraDark'
    | 'neutralDark'
    | 'neutralMediumHigh'
    | 'neutralMediumLow'
    | 'neutralLight'
    | 'neutralUltraLight'
    | 'statusSuccessStandard'
    | 'statusSuccessAlt'
    | 'statusAlertStandard'
    | 'statusAlertAlt'
    | 'statusErrorStandard'
    | 'statusErrorAlt';
  variant?:
    | 'l1'
    | 'l2'
    | 'l3'
    | 'l4'
    | 'l5'
    | 'l6'
    | 'bodyLargeStandard'
    | 'bodyLargeShort'
    | 'bodyMediumStandard'
    | 'bodyMediumShort'
    | 'auxiliarSmall'
    | 'auxiliarMini'
    | 'overline'
    | 'action';
  textAlign?: 'left' | 'center' | 'right';
}

interface BaseValuesInterface {
  [key: string]: TypographyProps;
}

const componentBaseValues = (theme: DefaultTheme) => ({
  l1: {
    fontSize: 4,
    lineHeight: 4.5,

    fontSizeDesktop: 6,
    lineHeightDesktop: 6.5,

    fontFamily: theme.fonts.primary,
    fontWeight: theme.weights.light,
  },

  l2: {
    fontSize: 3,
    lineHeight: 3.5,

    fontSizeDesktop: 4.5,
    lineHeightDesktop: 5,

    fontFamily: theme.fonts.primary,
    fontWeight: theme.weights.light,
  },

  l3: {
    fontSize: 2.5,
    lineHeight: 3,

    fontSizeDesktop: 3.5,
    lineHeightDesktop: 4,

    fontFamily: theme.fonts.primary,
    fontWeight: theme.weights.light,
  },

  l4: {
    fontSize: 2,
    lineHeight: 2.5,

    fontSizeDesktop: 2.5,
    lineHeightDesktop: 3,

    fontFamily: theme.fonts.primary,
    fontWeight: theme.weights.light,
  },

  l5: {
    fontSize: 1.5,
    lineHeight: 2,

    fontSizeDesktop: 2,
    lineHeightDesktop: 2.5,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  l6: {
    fontSize: 1.25,
    lineHeight: 1.5,

    fontSizeDesktop: 1.5,
    lineHeightDesktop: 2,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  bodyLargeStandard: {
    fontSize: 1,
    lineHeight: 1.5,

    fontSizeDesktop: 1,
    lineHeightDesktop: 1.5,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  bodyLargeShort: {
    fontSize: 1,
    lineHeight: 1.25,

    fontSizeDesktop: 1,
    lineHeightDesktop: 1.25,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  bodyMediumStandard: {
    fontSize: 0.875,
    lineHeight: 1.5,

    fontSizeDesktop: 0.875,
    lineHeightDesktop: 1.5,

    fontFamily: theme.fonts.primary,
    fontWeight: theme.weights.light,
  },

  bodyMediumShort: {
    fontSize: 0.875,
    lineHeight: 1,

    fontSizeDesktop: 0.875,
    lineHeightDesktop: 1,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  auxiliarSmall: {
    fontSize: 0.75,
    lineHeight: 1,

    fontSizeDesktop: 0.75,
    lineHeightDesktop: 1,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  auxiliarMini: {
    fontSize: 0.625,
    lineHeight: 1,

    fontSizeDesktop: 0.625,
    lineHeightDesktop: 1,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.light,
  },

  overline: {
    fontSize: 0.625,
    lineHeight: 1,

    fontSizeDesktop: 0.625,
    lineHeightDesktop: 1,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.standard,

    letterSpacing: 0.08,
    textTransform: 'uppercase',
  },

  action: {
    fontSize: 0.875,
    lineHeight: 1,

    fontSizeDesktop: 0.875,
    lineHeightDesktop: 1,

    fontFamily: theme.fonts.secondary,
    fontWeight: theme.weights.standard,

    letterSpacing: 0.04,
    textTransform: 'uppercase',
  },
});

const baseValueStyles = (
  baseValues: TypographyProps,
  componentProps: ComponentProps,
) => css`
  font-size: ${baseValues.fontSize}rem;
  line-height: ${props => props.theme.lineHeight * baseValues.lineHeight}px;

  font-family: ${baseValues.fontFamily};
  font-weight: ${baseValues.fontWeight};

  color: ${props =>
    componentProps?.color
      ? props.theme.colors[componentProps?.color]
      : props.theme.colors.neutralDark};
  margin: ${componentProps?.margin || 0};

  letter-spacing: ${baseValues?.letterSpacing || 0}rem;
  text-transform: ${baseValues?.textTransform || 'none'};

  text-align: ${componentProps?.textAlign || 'left'};

  ${media.tablet`
    font-size: ${baseValues.fontSizeDesktop}rem;
    line-height: ${props =>
      props.theme.lineHeight * baseValues.lineHeightDesktop}px;
  `};
`;

export const getTextStyle = (
  { variant = 'bodyMediumStandard', ...rest }: ComponentProps,
  theme: DefaultTheme,
) => {
  const baseValues: BaseValuesInterface = componentBaseValues(theme);

  return baseValueStyles(baseValues[variant], rest);
};

const Text = styled.p<ComponentProps>`
  ${props => getTextStyle(props, props.theme)};
`;

export default Text;
